import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { initiateSocketConnection } from 'cfgCommon/socketClient'

export const SocketContext = createContext()

export const SOCKET_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  WHATSAPP_MESSAGE: 'whatsapp-message',
  NOTIFICATION: 'notification',
  LEAD_ASSIGNED: 'LEAD_ASSIGNED',
  JOIN_AUTO_ASSIGN_ROOM: 'joinAutoAssignRoom',
  LEAVE_AUTO_ASSIGN_ROOM: 'leaveAutoAssignRoom',
  WHATSAPP_INITIATE_OUTBOUND_CALL: 'whatsapp_initiate_outbound_call',
  WHATSAPP_INITIATE_OUTBOUND_CALL_FAILED: 'whatsapp_initiate_outbound_call_failed',
  WHATSAPP_OUTBOUND_CALL_CONNECTED: 'whatsapp_outbound_call_connected',
  WHATSAPP_OUTBOUND_CALL_RINGING: 'whatsapp_outbound_call_ringing',
  WHATSAPP_OUTBOUND_CALL_ANSWERED: 'whatsapp_outbound_call_answered',
  WHATSAPP_CLIENT_ENDED_CALL: 'whatsapp_client_ended_call',
  WHATSAPP_END_CALL: 'whatsapp_end_call',
  REDIRECT_USER: 'redirectUser'
}

const PENDING_WHATSAPP_CALL_KEY = 'asofix_pending_whatsapp_call'

// Una sola vez por carga de página: evita cortar la llamada ante reconnects de socket.io.
let didTerminatePendingCallThisPageLoad = false

export const savePendingWhatsappCall = ({ company_id, wa_phone_id, callId }) => {
  if (!company_id || !wa_phone_id || !callId) return
  try {
    sessionStorage.setItem(
      PENDING_WHATSAPP_CALL_KEY,
      JSON.stringify({ company_id, wa_phone_id, callId })
    )
  } catch (_) {
    // ignore
  }
}

export const clearPendingWhatsappCall = () => {
  try {
    sessionStorage.removeItem(PENDING_WHATSAPP_CALL_KEY)
  } catch (_) {
    // ignore
  }
}

const readPendingWhatsappCall = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_WHATSAPP_CALL_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

const terminatePendingWhatsappCallIfNeeded = socket => {
  if (didTerminatePendingCallThisPageLoad || !socket) return
  didTerminatePendingCallThisPageLoad = true

  const pending = readPendingWhatsappCall()
  if (!pending?.callId || !pending?.company_id || !pending?.wa_phone_id) {
    clearPendingWhatsappCall()
    return
  }

  socket.emit(SOCKET_EVENTS.WHATSAPP_END_CALL, pending)
  clearPendingWhatsappCall()
}

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const socketRef = useRef(null)
  const token = useSelector((state) => state.common?.user?.token)

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
      setIsConnected(false)
      return
    }

    if (socketRef.current) return

    const socket = initiateSocketConnection(token)
    socketRef.current = socket
    
    const handleConnect = () => {
      console.log('socket connected:', socket.id)
      setIsConnected(true)
      terminatePendingWhatsappCallIfNeeded(socket)
    }

    const handleDisconnect = (reason) => {
      console.log('! socket disconnected:', reason)
      setIsConnected(false)
    }

    socket.on(SOCKET_EVENTS.CONNECT, handleConnect)
    socket.on(SOCKET_EVENTS.DISCONNECT, handleDisconnect)

    // Si el socket ya conectó antes de suscribirnos
    if (socket.connected) {
      terminatePendingWhatsappCallIfNeeded(socket)
    }

    return () => {
      if (socket) {
        socket.off(SOCKET_EVENTS.CONNECT, handleConnect)
        socket.off(SOCKET_EVENTS.DISCONNECT, handleDisconnect)
        socket.disconnect()
      }
      socketRef.current = null
      setIsConnected(false)
    }
  }, [token])

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, isConnected, SOCKET_EVENTS }}>
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = () => useContext(SocketContext)
